import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  doc, 
  updateDoc, 
  getDoc,
  writeBatch,
  Timestamp,
  deleteField
} from 'firebase/firestore';
import { db } from '../config';
import type { Group } from '@/types/support';
import { getGroupMembers } from './group-members';
import { 
  propagateMemberChanges, 
  handleMemberChangeWithValidation,
  forceFullGroupSync 
} from './hierarchical-propagation';

/**
 * Get all child groups of a parent group
 */
export async function getChildGroups(parentGroupId: string): Promise<Group[]> {
  try {
    const childGroupsQuery = query(
      collection(db, 'supportGroups'),
      where('parentGroupId', '==', parentGroupId)
    );
    
    const snapshot = await getDocs(childGroupsQuery);
    const childGroups = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    })) as Group[];

    // Sort by name after fetching
    childGroups.sort((a, b) => a.name.localeCompare(b.name));
    
    return childGroups;
  } catch (error) {
    console.error('Error fetching child groups:', error);
    throw error;
  }
}

/**
 * Get all top-level groups (groups without a parent) for an organization
 */
export async function getTopLevelGroups(organizationId: string): Promise<Group[]> {
  try {
    // First get all groups for the organization
    const allGroupsQuery = query(
      collection(db, 'supportGroups'),
      where('organization', '==', organizationId)
    );
    
    const snapshot = await getDocs(allGroupsQuery);
    const allGroups = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    })) as Group[];

    // Filter for top-level groups (no parent) and sort by name
    const topLevelGroups = allGroups
      .filter(group => !group.parentGroupId)
      .sort((a, b) => a.name.localeCompare(b.name));
    
    return topLevelGroups;
  } catch (error) {
    console.error('Error fetching top-level groups:', error);
    throw error;
  }
}

/**
 * Get the complete hierarchy tree for an organization
 */
export async function getGroupHierarchy(organizationId: string): Promise<(Group & { children: Group[] })[]> {
  try {
    // Get all groups for the organization
    const allGroupsQuery = query(
      collection(db, 'supportGroups'),
      where('organization', '==', organizationId)
    );
    
    const snapshot = await getDocs(allGroupsQuery);
    const allGroups = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    })) as Group[];

    // Sort groups by name after fetching
    allGroups.sort((a, b) => a.name.localeCompare(b.name));

    // Build hierarchy
    const groupMap = new Map<string, Group & { children: Group[] }>();
    const topLevelGroups: (Group & { children: Group[] })[] = [];

    // Initialize all groups with empty children arrays
    allGroups.forEach(group => {
      groupMap.set(group.id, { ...group, children: [] });
    });

    // Build the hierarchy
    allGroups.forEach(group => {
      const groupWithChildren = groupMap.get(group.id)!;
      
      if (group.parentGroupId && groupMap.has(group.parentGroupId)) {
        // Add to parent's children
        const parent = groupMap.get(group.parentGroupId)!;
        parent.children.push(groupWithChildren);
      } else {
        // Top-level group
        topLevelGroups.push(groupWithChildren);
      }
    });

    // Sort children arrays by name as well
    const sortChildren = (groups: (Group & { children: Group[] })[]) => {
      groups.sort((a, b) => a.name.localeCompare(b.name));
      groups.forEach(group => sortChildren(group.children));
    };
    
    sortChildren(topLevelGroups);

    return topLevelGroups;
  } catch (error) {
    console.error('Error fetching group hierarchy:', error);
    throw error;
  }
}

/**
 * Move a group to a new parent (or make it top-level by passing null)
 */
export async function moveGroupToParent(
  groupId: string, 
  newParentId: string | null,
  userId: string
): Promise<void> {
  try {
    const groupRef = doc(db, 'supportGroups', groupId);
    const groupDoc = await getDoc(groupRef);
    
    if (!groupDoc.exists()) {
      throw new Error('Group not found');
    }

    const groupData = groupDoc.data();
    

    
    // Prevent circular references
    if (newParentId) {
      const isCircular = await checkCircularReference(groupId, newParentId);
      if (isCircular) {
        throw new Error('Cannot move group: This would create a circular reference');
      }
    }

    // Update the group's parent
    await updateDoc(groupRef, {
      parentGroupId: newParentId,
      updatedAt: Timestamp.now()
    });

    // 🚀 Enhanced synchronization for group move
    try {
      // Force full sync for the moved group and all affected groups
      await forceFullGroupSync(groupId);
      
      if (newParentId) {
        await handleMemberChangeWithValidation(newParentId, 'move');
      }
      
      // Also update the old parent if it exists
      if (groupData.parentGroupId) {
        await handleMemberChangeWithValidation(groupData.parentGroupId, 'move');
      }
      
      console.log(`✅ Completed enhanced synchronization for group move`);
    } catch (error) {
      console.warn('⚠️ Failed to synchronize changes after group move, attempting fallback:', error);
      // Fallback to basic propagation
      try {
        await propagateMemberChanges(groupId);
        if (newParentId) {
          await propagateMemberChanges(newParentId);
        }
        if (groupData.parentGroupId) {
          await propagateMemberChanges(groupData.parentGroupId);
        }
      } catch (fallbackError) {
        console.error('⚠️ Fallback propagation also failed:', fallbackError);
      }
    }

    console.log(`Group ${groupId} moved to parent ${newParentId || 'top-level'}`);
  } catch (error) {
    console.error('Error moving group:', error);
    throw error;
  }
}

/**
 * Put a subgroup under another section of its organization, or take it out
 * of any section (`sectionId` null clears parentGroupId). Used to empty a
 * section before deleting it (owner decision #4). The target must be a
 * section (groupType 'parent') of the same organization.
 */
export async function setSubgroupSection(groupId: string, sectionId: string | null): Promise<void> {
  const groupRef = doc(db, 'supportGroups', groupId);
  const groupSnap = await getDoc(groupRef);
  if (!groupSnap.exists()) throw new Error('Group not found');
  if (sectionId) {
    if (sectionId === groupId) throw new Error('A group cannot be its own section');
    const sectionSnap = await getDoc(doc(db, 'supportGroups', sectionId));
    const section = sectionSnap.exists() ? sectionSnap.data() : null;
    if (!section || section.groupType !== 'parent') throw new Error('Target is not a section');
    if ((section.organization || null) !== (groupSnap.data().organization || null)) {
      throw new Error('Target section belongs to another organization');
    }
  }
  await updateDoc(groupRef, {
    parentGroupId: sectionId ? sectionId : deleteField(),
    updatedAt: Timestamp.now(),
  });
}

/**
 * Check if moving a group would create a circular reference
 */
async function checkCircularReference(groupId: string, potentialParentId: string): Promise<boolean> {
  let currentId = potentialParentId;
  const visited = new Set<string>();

  while (currentId && !visited.has(currentId)) {
    if (currentId === groupId) {
      return true; // Circular reference detected
    }

    visited.add(currentId);
    
    // Get the parent of current group
    const groupDoc = await getDoc(doc(db, 'supportGroups', currentId));
    if (!groupDoc.exists()) {
      break;
    }
    
    currentId = groupDoc.data().parentGroupId;
  }

  return false;
}

/**
 * Get all descendant groups (children, grandchildren, etc.) of a parent group
 */
export async function getAllDescendantGroups(parentGroupId: string): Promise<Group[]> {
  const descendants: Group[] = [];
  const toProcess = [parentGroupId];
  const processed = new Set<string>();

  while (toProcess.length > 0) {
    const currentParentId = toProcess.shift()!;
    if (processed.has(currentParentId)) continue;
    
    processed.add(currentParentId);
    const children = await getChildGroups(currentParentId);
    
    descendants.push(...children);
    toProcess.push(...children.map(child => child.id));
  }

  return descendants;
}

/**
 * Get aggregated member count for a group including all its descendants.
 * Organization managers are counted like any member, as on mobile.
 */
export async function getAggregatedMemberCount(groupId: string): Promise<number> {
  try {
    let totalMembers = (await getGroupMembers(groupId)).length;
    const descendants = await getAllDescendantGroups(groupId);
    for (const descendant of descendants) {
      totalMembers += (await getGroupMembers(descendant.id)).length;
    }
    return totalMembers;
  } catch (error) {
    console.error('Error calculating aggregated member count:', error);
    throw error;
  }
}

/**
 * ❌ DEPRECATED: This function updated denormalized member counts
 * Now using pure virtual membership - no sync operations needed
 */
export async function updateParentGroupCounts(groupId: string): Promise<void> {
  console.log(`⚠️ updateParentGroupCounts(${groupId}) called but deprecated - using virtual membership instead`);
  // No-op: Virtual membership system handles this automatically
}